import { useSyncExternalStore } from "react";

// Max characters in one composed message. Mirrors the BE's own cap (it rejects
// a longer body with a 400 `BAD_USER_INPUT`), so we stop the visitor at the
// same bound rather than let them compose something that can only fail to send.
// Also guards against a runaway paste (a million lines would freeze the
// contentEditable). The BE stays the security bound - this is the UX mirror.
export const MAX_COMPOSER_CHARS = 4000;
// Show the live counter only once the draft gets close to the cap.
export const COUNTER_VISIBLE_FROM = MAX_COMPOSER_CHARS - 500;

export interface DraftSnapshot {
  /** The composer's PLAIN-TEXT projection (empty state, typing signal, send guard). */
  text: string;
  /** The sanitisable HTML projection that is actually sent when formatted. */
  html: string;
}

export interface DraftStore {
  get(): DraftSnapshot;
  set(text: string, html: string): void;
  subscribe(listener: () => void): () => void;
}

/**
 * The composer draft lives OUTSIDE React state so a keystroke does not
 * re-render the conversation: the tab reads it on send, and only the few
 * controls that depend on it (send/mic button, char counter) subscribe via
 * `useDraft` with a selector.
 */
export function createDraftStore(initial?: DraftSnapshot): DraftStore {
  let snapshot: DraftSnapshot = initial ?? { text: "", html: "" };
  const listeners = new Set<() => void>();
  return {
    get: () => snapshot,
    set(text, html) {
      if (snapshot.text === text && snapshot.html === html) return;
      snapshot = { text, html };
      for (const l of [...listeners]) l();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** Subscribe to a derived value of the draft; re-renders only when it changes. */
export function useDraft<T>(store: DraftStore, select: (s: DraftSnapshot) => T): T {
  return useSyncExternalStore(
    store.subscribe,
    () => select(store.get()),
    () => select(store.get()),
  );
}
